import React, { useState } from 'react';
import { SmilePlus } from 'lucide-react';
import { groupReactions, type MessageReaction } from './emoji';
import { REACTION_ICONS, REACTION_KEYS, REACTION_LABEL } from './reactionIcons';
import { TYPE } from '../../constants/typeScale';

/**
 * Message reactions rendered as MONOCHROME Lucide icons (not colorful emoji) so the chat stays
 * grayscale and generated media is what pops — a Medium-clap aesthetic: a small icon + count,
 * with the originators' display names STACKED in the hover tooltip when several react the same
 * way. Your own reactions are highlighted; clicking one you're in toggles it off. A "+" opens
 * the picker. Humans and agents both react (an agent's name shows on hover like anyone else).
 */
const MessageReactions: React.FC<{
  reactions?: MessageReaction[];
  myId?: string;
  align?: 'start' | 'end';
  onToggle: (key: string) => void;
}> = ({ reactions, myId, align = 'start', onToggle }) => {
  const [picking, setPicking] = useState(false);
  const groups = groupReactions(reactions, myId).filter((g) => REACTION_ICONS[g.emoji]);

  return (
    <div className={`relative flex flex-wrap items-center gap-1 ${align === 'end' ? 'justify-end' : ''}`} data-testid="msg-reactions">
      {groups.map((g) => {
        const Icon = REACTION_ICONS[g.emoji];
        return (
          <button
            key={g.emoji}
            type="button"
            title={g.names.join('\n')}
            onClick={() => onToggle(g.emoji)}
            className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 ${TYPE.control} transition-colors ${
              g.mine
                ? 'border-secondary-400 bg-secondary-100 text-secondary-900 dark:border-secondary-500 dark:bg-secondary-700 dark:text-white'
                : 'border-secondary-200 bg-secondary-50 text-secondary-500 hover:bg-secondary-100 dark:border-secondary-600 dark:bg-secondary-800 dark:text-secondary-300 dark:hover:bg-secondary-700'
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            <span>{g.count}</span>
          </button>
        );
      })}
      <button
        type="button"
        aria-label="Add reaction" title="Add reaction"
        onClick={() => setPicking((v) => !v)}
        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-secondary-400 hover:bg-secondary-100 hover:text-secondary-700 dark:hover:bg-secondary-800 dark:hover:text-secondary-200"
      >
        <SmilePlus className="h-3.5 w-3.5" />
      </button>

      {picking && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setPicking(false)} />
          <div className={`absolute z-20 bottom-full mb-1 ${align === 'end' ? 'right-0' : 'left-0'} flex gap-0.5 rounded-xl border border-secondary-200 bg-white p-1.5 shadow-lg dark:border-secondary-600 dark:bg-secondary-900`} data-testid="reaction-picker">
            {REACTION_KEYS.map((k) => {
              const Icon = REACTION_ICONS[k];
              return (
                <button key={k} type="button" title={REACTION_LABEL[k]} aria-label={REACTION_LABEL[k]}
                  onClick={() => { onToggle(k); setPicking(false); }}
                  className="rounded-md p-1.5 text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900 dark:text-secondary-300 dark:hover:bg-secondary-800 dark:hover:text-white">
                  <Icon className="h-4 w-4" />
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default MessageReactions;
